import React, { useEffect, useState } from "react";
import { X, Sparkles, ChevronRight, RefreshCw, Play } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { TrainingBadge } from "./TrainingBadge";
import { Select } from "../shared/Pickers";

const FOCUS_OPTIONS     = ["Mixed", "Shooting", "Ball Handling", "Finishing", "Attacking", "Tactical & Passing"];
const DURATION_OPTIONS  = [15, 30, 45, 60, 90];
const SKILL_OPTIONS     = ["All Levels", "Youth", "Middle School", "High School", "College", "Pro"];
const AGE_OPTIONS       = ["Any", "8–10", "11–13", "14–15", "16–18", "College", "Pro"];
const ARCHETYPE_OPTIONS = ["Any", "Point Guard", "Combo Guard", "Wing", "Forward", "Stretch Big", "Big"];

const DEFAULT_INPUTS = {
  focus: "Mixed",
  duration: 60,
  skill: "All Levels",
  ageRange: "Any",
  archetype: "Any",
};

export function CreateYourWorkoutModal({ C, dark, open, onClose, onOpenDrill, onBuildPlan }) {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  // Load saved defaults on first open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from("profiles").select("last_workout_inputs").eq("id", user.id).limit(1);
        const saved = data?.[0]?.last_workout_inputs;
        if (saved && typeof saved === "object") {
          setInputs({ ...DEFAULT_INPUTS, ...saved });
        }
      } catch {}
    })();
  }, [open]);

  if (!open) return null;

  const update = (k) => (e) => setInputs({ ...inputs, [k]: e.target.value });
  const updateNum = (k) => (e) => setInputs({ ...inputs, [k]: Number(e.target.value) });

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch("/api/admin-actions?action=build-custom-workout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(inputs),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || res.statusText);
      setResult(json);
    } catch (e) {
      setErr(e.message || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = {
    width: "100%", padding: "10px 12px", fontSize: 13,
    background: dark ? "rgba(255,255,255,0.04)" : "#F5F5F5",
    border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
    outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 10, fontWeight: 800, color: C.textDim,
    letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6,
  };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:5500,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)"}}/>
      <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:5501,
        width:"92%",maxWidth:520,maxHeight:"88vh",overflow:"auto",
        background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:16,
        boxShadow:dark?"0 24px 80px rgba(0,0,0,0.6)":"0 20px 60px rgba(0,0,0,0.2)",
        padding:"22px 24px",fontFamily:"'DM Sans', sans-serif",color:C.text}}>

        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <div style={{width:32,height:32,borderRadius:8,background:`${GOLD}22`,border:`1px solid ${GOLD}66`,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Sparkles size={16} color={GOLD}/>
          </div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.4,textTransform:"uppercase"}}>Workout Builder</span>
              <TrainingBadge />
            </div>
            <div style={{fontSize:17,fontWeight:800}}>Create your workout</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{background:"transparent",border:"none",cursor:"pointer",padding:6,color:C.textMid}}>
            <X size={17}/>
          </button>
        </div>
        <div style={{fontSize:12.5,color:C.textMid,marginBottom:18,lineHeight:1.5}}>
          Pick your inputs and we'll assemble a session from the library. Your last inputs save automatically.
        </div>

        {!result ? (
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              <div>
                <label style={labelStyle}>Focus</label>
                <Select value={inputs.focus} onChange={update("focus")} style={fieldStyle}>
                  {FOCUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </div>
              <div>
                <label style={labelStyle}>Time available</label>
                <Select value={inputs.duration} onChange={updateNum("duration")} style={fieldStyle}>
                  {DURATION_OPTIONS.map(o => <option key={o} value={o}>{o} min</option>)}
                </Select>
              </div>
              <div>
                <label style={labelStyle}>Skill level</label>
                <Select value={inputs.skill} onChange={update("skill")} style={fieldStyle}>
                  {SKILL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </div>
              <div>
                <label style={labelStyle}>Age range</label>
                <Select value={inputs.ageRange} onChange={update("ageRange")} style={fieldStyle}>
                  {AGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </div>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={labelStyle}>Position / archetype</label>
                <Select value={inputs.archetype} onChange={update("archetype")} style={fieldStyle}>
                  {ARCHETYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              </div>
            </div>

            {err && (
              <div style={{padding:"10px 12px",borderRadius:9,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.35)",color:"#F87171",marginBottom:12,fontSize:12.5,lineHeight:1.5}}>{err}</div>
            )}

            <button onClick={generate} disabled={busy}
              style={{width:"100%",padding:"12px 0",borderRadius:10,fontSize:14,fontWeight:800,cursor:busy?"default":"pointer",
                border:"none",background:GOLD,color:"#111",boxShadow:`0 4px 14px ${GOLD}44`,
                display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,opacity:busy?.7:1}}>
              {busy ? <><RefreshCw size={14} style={{animation:"spin 1s linear infinite"}}/> Building…</> : <>Generate workout <ChevronRight size={14}/></>}
            </button>
          </>
        ) : (
          <>
            <div style={{padding:"14px 16px",borderRadius:12,background:`linear-gradient(135deg, ${GOLD}14, ${GOLD}04)`,border:`1px solid ${GOLD}55`,marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",marginBottom:4}}>{result.focus} · {result.duration} min</div>
              <div style={{fontSize:17,fontWeight:800,marginBottom:4,lineHeight:1.25}}>{result.title}</div>
              {result.summary && <div style={{fontSize:12.5,color:C.textMid,lineHeight:1.55}}>{result.summary}</div>}
            </div>

            <div style={{marginBottom:14}}>
              {result.drills.map((d, i) => (
                <div key={d.id} onClick={()=>onOpenDrill && onOpenDrill(d)}
                  className="btn"
                  style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",marginBottom:6,
                    borderRadius:10,border:`1px solid ${C.border}`,background:dark?"rgba(255,255,255,0.02)":"#FAFAFA",
                    cursor:onOpenDrill?"pointer":"default"}}>
                  <div style={{flexShrink:0,width:26,height:26,borderRadius:"50%",background:`${GOLD}22`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:GOLD}}>{i+1}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{d.title}</div>
                    <div style={{fontSize:11,color:C.textDim,marginTop:2}}>{d.category}{d.type ? ` · ${d.type}` : ""}{d.duration ? ` · ${typeof d.duration === "number" ? `${Math.floor(d.duration/60)}:${String(d.duration%60).padStart(2,"0")}` : d.duration}` : ""}</div>
                  </div>
                  <Play size={12} color={C.textDim}/>
                </div>
              ))}
            </div>

            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button onClick={()=>setResult(null)}
                style={{flex:1,minWidth:120,padding:"10px 0",borderRadius:9,fontSize:12.5,fontWeight:700,cursor:"pointer",
                  border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,
                  display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <RefreshCw size={12}/> Tweak & regenerate
              </button>
              {onBuildPlan && (
                <button onClick={()=>onBuildPlan(result)}
                  style={{flex:2,minWidth:140,padding:"10px 0",borderRadius:9,fontSize:12.5,fontWeight:800,cursor:"pointer",
                    border:"none",background:GOLD,color:"#111",boxShadow:`0 4px 14px ${GOLD}44`,
                    display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  Build as practice plan <ChevronRight size={13}/>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CreateYourWorkoutModal;
